﻿import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Popconfirm, Spin, Tag, message } from 'antd';
import { mFetch, MApiError, useMAuth } from '../mApi';
import { MPageTitle } from '../MobileApp';

interface BindingInfo {
  bound: boolean;
  openidMasked?: string;
  nickname?: string | null;
  boundAt?: string;
  lastLoginAt?: string;
}

interface MpStatus {
  mode: 'real' | 'mock' | 'disabled';
  appIdMasked: string;
  tokenOk: boolean | null;
  tokenError?: string | null;
  tokenHint?: string | null;
  bindingCount: number;
  draftCount: number;
}

export default function MMe() {
  const user = useMAuth((s) => s.user);
  const clear = useMAuth((s) => s.clear);
  const nav = useNavigate();
  const [binding, setBinding] = useState<BindingInfo | null>(null);
  const [status, setStatus] = useState<MpStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([
        mFetch<BindingInfo>('/wechat-mp/me'),
        mFetch<MpStatus>('/wechat-mp/status').catch(() => null),
      ]);
      setBinding(b);
      setStatus(s);
    } catch (e) {
      message.error(e instanceof MApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const unbind = async () => {
    try {
      await mFetch('/wechat-mp/me', { method: 'DELETE' });
      message.success('已解除微信绑定');
      load();
    } catch (e) {
      message.error(e instanceof MApiError ? e.message : String(e));
    }
  };

  const bindNow = () => {
    nav('/m/login?bind=1');
  };

  return (
    <div>
      <MPageTitle title="我的" />
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : (
        <div style={{ padding: '8px 16px' }}>
          <Card size="small" style={{ background: '#141428', borderColor: '#26264a' }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{user?.username}</div>
            <div style={{ fontSize: 12, color: '#8a8aa3', marginTop: 4 }}>角色：{user?.roleCode}</div>
          </Card>

          <Card size="small" style={{ background: '#141428', borderColor: '#26264a', marginTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>微信绑定</span>
              {binding?.bound ? <Tag color="green">已绑定</Tag> : <Tag>未绑定</Tag>}
            </div>
            {binding?.bound ? (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 13, color: '#b5b5c9' }}>{binding.openidMasked} {binding.nickname ? `· ${binding.nickname}` : ''}</div>
                <div style={{ fontSize: 12, color: '#5c5c75', marginTop: 4 }}>
                  绑定于 {binding.boundAt ? new Date(binding.boundAt).toLocaleDateString('zh-CN') : '-'}
                </div>
                <Popconfirm title="解除后需重新授权才能一键登录" onConfirm={unbind} okText="解除" cancelText="取消">
                  <Button size="small" danger style={{ marginTop: 8 }} data-testid="m-unbind">解除绑定</Button>
                </Popconfirm>
              </div>
            ) : (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, color: '#8a8aa3', marginBottom: 8 }}>绑定后可在微信内一键登录管理网站</div>
                <Button size="small" type="primary" onClick={bindNow} data-testid="m-bind-now">立即绑定</Button>
              </div>
            )}
          </Card>

          {status && (
            <Card size="small" style={{ background: '#141428', borderColor: '#26264a', marginTop: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>公众号通道</div>
              <div style={{ fontSize: 13, color: '#b5b5c9', lineHeight: 1.9 }}>
                <div>模式：{status.mode === 'real' ? '正式（已配凭据）' : status.mode === 'mock' ? '模拟（本地联调）' : '未配置'}</div>
                {status.appIdMasked ? <div>AppID：{status.appIdMasked}</div> : null}
                {status.mode === 'real' && <div>token：{status.tokenOk ? '✅ 正常' : '❌ 异常'}</div>}
                {status.tokenError ? (
                  <Alert type="warning" showIcon style={{ marginTop: 6 }} title={status.tokenError} description={status.tokenHint || undefined} />
                ) : null}
                <div>已绑定微信：{status.bindingCount} 人 · 草稿/发布记录：{status.draftCount} 条</div>
              </div>
            </Card>
          )}

          <Button
            danger
            block
            style={{ marginTop: 16 }}
            data-testid="m-logout"
            onClick={() => {
              clear();
              nav('/m/login');
            }}
          >
            退出登录
          </Button>
        </div>
      )}
    </div>
  );
}
